import { format, parseISO } from "date-fns";
import { stringToDate } from "helpers/stringToDate";
import React, { useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";

export type CellType =
  | "default"
  | "checkboxCell"
  | "buttonCell"
  | "filterCell"
  | "rowOptionCell"
  | "SingleSelect"
  | "MultiSelect"
  | "ProgressBar"
  | "ViewDocument";

export type CellOptions = {
  boldText?: boolean;
  greyText?: boolean;
  noPaddingLeft?: boolean;
  noPaddingRight?: boolean;
  noSortIcon?: boolean;
  overflowVisible?: boolean;
  additionalClassNames?: string;
  cellType?: CellType;
  width?:
    | "Small"
    | "Medium"
    | "Medium2"
    | "Large"
    | "Large2"
    | "SemiLarge"
    | "SemiLarge2"
    | "SemiLarge3"
    | "SemiSmall"
    | "SemiSmall2"
    | "Mini"
    | "Huge"
    | "Huge2"
    | "Huge3";
  DSIH?: boolean;
  style?: React.CSSProperties;
  label?: string;
};

export type CellProps = {
  text?: string;
  title?: string;
  options?: CellOptions;
  sortable?: boolean;
  href?: string | null;
  onClick?(e: React.MouseEvent): void;
  tooltipLabel?: string;
  cellDataType?: string;
  overrideWidth?: number | null;
  noTooltip?: boolean;
};

type CellTooltipProps = {
  text: string;
  anchorSelect: string; // className of element it will be a tooltip for
};
const CellTooltip: React.FC<CellTooltipProps> = ({ text, anchorSelect }) => {
  return (
    <Tooltip anchorSelect={`.${anchorSelect.replace(/\s/g, ".")}`} place="top">
      {text}
    </Tooltip>
  );
};

type CellTooltipV2Props = {
  text: string;
};
const CellTooltipV2: React.FC<CellTooltipV2Props> = ({ text }) => {
  return (
    <div className="CellTooltip">
      <div className="Text">{text}</div>

      <div className="Arrow"></div>
    </div>
  );
};

const Cell: React.FC<CellProps> = ({
  children,
  text,
  title = "",
  options,
  href,
  sortable,
  onClick,
  tooltipLabel,
  cellDataType,
  overrideWidth,
  noTooltip,
}) => {
  const [cellClassName, setCellClassName] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const [parsedTooltipLabel, setParsedTooltipLabel] = useState<string | null>(
    null
  );

  const getCellClassName = () => {
    let className = "Cell"; //`Cell ${options?.label}`;
    if (options) {
      const {
        boldText,
        greyText,
        noPaddingLeft,
        noPaddingRight,
        noSortIcon,
        overflowVisible,
        additionalClassNames,
        cellType,
        width,
        label,
      } = options;

      if (boldText) {
        className += " BoldText";
      }
      if (greyText) {
        className += " GreyText";
      }
      if (noPaddingLeft) {
        className += " NoPaddingLeft";
      }
      if (noPaddingRight) {
        className += " NoPaddingRight";
      }
      if (cellType === "buttonCell") {
        className += " ButtonCell";
      }
      if (noSortIcon) {
        className += " NoSortIcon";
      }
      if (overflowVisible) {
        className += " OverflowVisible";
      }
      if (cellType === "checkboxCell") {
        className += " CheckboxCell";
      }
      if (cellType === "rowOptionCell") {
        className += " RowOptionCell";
      }
      if (cellType === "filterCell") {
        className += " FilterCell";
      }
      if (cellType === "SingleSelect" || cellType === "MultiSelect") {
        className += " DropDownSelectCell";
      }
      if (cellType === "ProgressBar") {
        className += " ProgressBar";
      }
      if (cellType === "ViewDocument") {
        className += " ViewDocument";
      }
      if (width === "Small") {
        className += " Small";
      }

      if (width === "Large") {
        className += " Large";
      }
      if (width === "Large2") {
        className += " Large2";
      }
      if (width === "SemiSmall") {
        className += " SemiSmall";
      }
      if (width === "SemiLarge") {
        className += " SemiLarge";
      }
      if (width === "SemiSmall2") {
        className += " SemiSmall2";
      }
      if (width === "SemiLarge2") {
        className += " SemiLarge2";
      }
      if (width === "SemiLarge3") {
        className += " SemiLarge3";
      }
      if (width === "Medium") {
        className += " Medium";
      }
      if (width === "Medium2") {
        className += " Medium2";
      }
      if (width === "Mini") {
        className += " Mini";
      }

      if (width === "Huge") {
        className += " Huge";
      }

      if (width === "Huge2") {
        className += " Huge2";
      }

      if (width === "Huge3") {
        className += " Huge3";
      }

      if (additionalClassNames) {
        className += " " + additionalClassNames;
      }
    }

    if (cellDataType) {
      className += " " + cellDataType;
    }

    if (sortable) {
      className += " Sortable";
    }

    setCellClassName(className);
  };

  const cellTitle = text ? text : title;

  useEffect(() => {
    getCellClassName();
  }, []);

  useEffect(() => {
    if (tooltipLabel) {
      if (cellDataType && cellDataType === "DateTime") {
        const date = stringToDate(tooltipLabel);

        const time =
          tooltipLabel.split("T").length > 1
            ? tooltipLabel.split("T")[1].split(".")[0]
            : null;

        let parsedDate: Date | null = null;

        let parsedDate2: string | null = null;

        const offset = date ? date.getTimezoneOffset() * 60000 : null;

        if (date && offset !== null) {
          const tempDate = new Date(date.setHours(0, 0, 0));

          parsedDate = new Date(tempDate.getTime() - offset);
        }

        if (parsedDate) {
          const year = parsedDate.getUTCFullYear();
          const month = parsedDate.getUTCMonth() + 1;
          const day = parsedDate.getUTCDate();

          const yearString = `${year}`;
          let monthString = `${month}`;
          let dayString = `${day}`;

          if (month < 10) {
            monthString = `0${month}`;
          }

          if (day < 10) {
            dayString = `0${day}`;
          }

          parsedDate2 = `${yearString}-${monthString}-${dayString}`;

          // OLD method, causes timezone issues based on user's computer's set timezone:
          // const tempParsedDate2 = format(parseISO(parsedDate2), "dd-MMM-yyyy");

          let tempParsedDate2 = "";
          try {
            tempParsedDate2 = format(parseISO(parsedDate2), "dd-MMM-yyyy");
          } catch {
            tempParsedDate2 = `${yearString}-${monthString}-${dayString}`;
          }

          parsedDate2 = `${tempParsedDate2}:${time}`;
        }

        if (parsedDate) {
          setParsedTooltipLabel(parsedDate2);
        } else {
          setParsedTooltipLabel(tooltipLabel);
        }
      } else if (cellDataType && cellDataType === "Boolean") {
        setParsedTooltipLabel(
          tooltipLabel.toLowerCase() === "true" ? "Yes" : "No"
        );
      } else {
        setParsedTooltipLabel(tooltipLabel);
      }
    }
  }, [tooltipLabel]);

  const useOverrideWidth =
    overrideWidth && (!options || (options && !options.width));

  return href ? (
    options?.DSIH ? (
      <React.Fragment>
        {cellClassName && (
          <React.Fragment>
            <div
              className={cellClassName}
              title={cellTitle}
              style={{
                ...options.style,
                cursor: "pointer",
                minWidth: useOverrideWidth ? overrideWidth : undefined,
              }}
              onClick={() => window.open(href, "_blank")}
              dangerouslySetInnerHTML={{ __html: children as any }}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              {showTooltip && parsedTooltipLabel && !noTooltip && (
                <CellTooltipV2 text={parsedTooltipLabel} />
              )}
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    ) : (
      <React.Fragment>
        {cellClassName && (
          <React.Fragment>
            <div
              className={cellClassName}
              title={cellTitle}
              style={{
                ...options?.style,
                cursor: "pointer",
                minWidth: useOverrideWidth ? overrideWidth : undefined,
              }}
              onClick={() => window.open(href, "_blank")}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              {text && <span>{text}</span>}
              {children}

              {showTooltip && parsedTooltipLabel && !noTooltip && (
                <CellTooltipV2 text={parsedTooltipLabel} />
              )}
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    )
  ) : options?.DSIH ? (
    <React.Fragment>
      {cellClassName && (
        <React.Fragment>
          <div
            className={cellClassName}
            title={cellTitle}
            style={{
              ...options?.style,
              cursor: "pointer",
              minWidth: useOverrideWidth ? overrideWidth : undefined,
            }}
            dangerouslySetInnerHTML={{ __html: children as any }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {showTooltip && parsedTooltipLabel && !noTooltip && (
              <CellTooltipV2 text={parsedTooltipLabel} />
            )}
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  ) : (
    <React.Fragment>
      {cellClassName && (
        <React.Fragment>
          <div
            className={cellClassName}
            title={cellTitle}
            style={{
              ...options?.style,
              minWidth: useOverrideWidth ? overrideWidth : undefined,
            }}
            onClick={onClick}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {text && <span>{text}</span>}
            {/* {`${children}`} */}
            {children}
            {showTooltip && parsedTooltipLabel && !noTooltip && (
              <CellTooltipV2 text={parsedTooltipLabel} />
            )}
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export { Cell };
